import { useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, STATUS_COLORS, ASSET_STATUS_LABELS, ASSET_TYPE_LABELS } from '../utils/api'
import { Asset } from '../types'
import { Edit2, Trash2, ArrowLeft, QrCode, Upload, X, ImageIcon, Wrench, Plus, Printer, ClipboardList, Paperclip, File, ExternalLink, RefreshCw, Package, Monitor, Download, Inbox } from 'lucide-react'
import QRCodeModal from '../components/QRCodeModal'
import ComponentsTab from '../components/ComponentsTab'
import DataSourceBadge from '../components/DataSourceBadge'
import { PRIORITY_CFG, STATUS_CFG, TicketPriority, TicketStatus } from './TicketsPage'

interface DeliveryAttachment { id:string; filename:string; originalName:string; url:string; size:number|null; mimeType:string|null; createdAt:string }

function DeliveryAttachPanel({ docId, attachments, onUploaded }: { docId:string; attachments:DeliveryAttachment[]; onUploaded:()=>void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const qc = useQueryClient()

  const deleteMut = useMutation({
    mutationFn: (attId:string) => api.delete(`/deliveries/${docId}/attachments/${attId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['deliveries','asset'] }); onUploaded() },
  })

  const uploadFiles = async (files: FileList|null) => {
    if (!files?.length) return
    setUploading(true)
    const fd = new FormData()
    Array.from(files).forEach(f => fd.append('files', f))
    try {
      await api.post(`/deliveries/${docId}/attachments`, fd, { headers:{ 'Content-Type':'multipart/form-data' } })
      qc.invalidateQueries({ queryKey: ['deliveries','asset'] })
      onUploaded()
    } catch(e:any) { alert(e.response?.data?.message||'อัปโหลดไม่สำเร็จ') }
    finally { setUploading(false) }
  }

  const isPdf = (m:string|null) => m==='application/pdf'
  const fmtSize = (s:number|null) => !s?'':s<1024*1024?`${(s/1024).toFixed(0)} KB`:`${(s/1024/1024).toFixed(1)} MB`

  return (
    <div className="mt-2 space-y-1.5">
      {attachments.map(att => (
        <div key={att.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group text-xs">
          <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${isPdf(att.mimeType)?'bg-red-100 text-red-600':'bg-blue-100 text-blue-600'}`}>
            {isPdf(att.mimeType)?<File size={12}/>:<ImageIcon size={12}/>}
          </div>
          <span className="flex-1 truncate text-gray-700">{att.originalName}</span>
          <span className="text-gray-400 flex-shrink-0">{fmtSize(att.size)}</span>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <a href={att.url} target="_blank" rel="noreferrer" className="p-1 text-gray-400 hover:text-blue-600 rounded"><ExternalLink size={12}/></a>
            <button onClick={()=>{ if(confirm('ลบไฟล์นี้?')) deleteMut.mutate(att.id) }} className="p-1 text-gray-400 hover:text-red-600 rounded"><X size={12}/></button>
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
          onChange={e=>uploadFiles(e.target.files)}/>
        <button onClick={()=>fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50">
          {uploading ? <RefreshCw size={12} className="animate-spin"/> : <Paperclip size={12}/>}
          {uploading?'กำลังอัปโหลด...':'แนบไฟล์ (PDF/รูปภาพ)'}
        </button>
      </div>
    </div>
  )
}

interface AssetFileItem { id:string; filename:string; originalName:string; url:string; size:number|null; mimeType:string|null; fileType:string; createdAt:string }

function AssetFilesPanel({ assetId, files, isAdmin, onChanged }: { assetId:string; files:AssetFileItem[]; isAdmin:boolean; onChanged:()=>void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const qc = useQueryClient()

  const deleteMut = useMutation({
    mutationFn: (fid:string) => api.delete(`/assets/files/${fid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['asset', assetId] }); onChanged() },
  })

  const uploadFiles = async (fileList: FileList|null) => {
    if (!fileList?.length) return
    setUploading(true)
    const fd = new FormData()
    Array.from(fileList).forEach(f => fd.append('files', f))
    fd.append('fileType', 'ATTACHMENT')
    try {
      await api.post(`/assets/${assetId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      qc.invalidateQueries({ queryKey: ['asset', assetId] })
      onChanged()
    } catch(e:any) { alert(e.response?.data?.message || 'อัปโหลดไม่สำเร็จ') }
    finally { setUploading(false) }
  }

  const fmtSize = (s:number|null) => !s?'':s<1024*1024?`${(s/1024).toFixed(0)} KB`:`${(s/1024/1024).toFixed(1)} MB`

  const winauditFiles = files.filter(f => f.fileType === 'WINAUDIT')
  const otherFiles = files.filter(f => f.fileType !== 'WINAUDIT')

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2">
          <Monitor size={14}/> ไฟล์ WinAudit & เอกสารแนบ
          {files.length > 0 && <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-xs">{files.length}</span>}
        </h2>
        {isAdmin && (
          <>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={e => uploadFiles(e.target.files)} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50">
              {uploading ? <RefreshCw size={13} className="animate-spin"/> : <Paperclip size={13}/>}
              {uploading ? 'กำลังอัปโหลด...' : 'แนบไฟล์'}
            </button>
          </>
        )}
      </div>

      {files.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-gray-300">
          <Monitor size={28} className="mb-2"/>
          <p className="text-sm text-gray-400">ยังไม่มีไฟล์ WinAudit หรือเอกสารแนบ</p>
        </div>
      ) : (
        <div className="space-y-2">
          {winauditFiles.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 font-medium mb-1.5 flex items-center gap-1"><Monitor size={11}/> WinAudit Reports</p>
              {winauditFiles.map(f => (
                <FileRow key={f.id} file={f} fmtSize={fmtSize} isAdmin={isAdmin} onDelete={() => { if(confirm('ลบไฟล์นี้?')) deleteMut.mutate(f.id) }} />
              ))}
            </div>
          )}
          {otherFiles.length > 0 && (
            <div>
              {winauditFiles.length > 0 && <p className="text-xs text-gray-400 font-medium mb-1.5 mt-2">เอกสารแนบอื่นๆ</p>}
              {otherFiles.map(f => (
                <FileRow key={f.id} file={f} fmtSize={fmtSize} isAdmin={isAdmin} onDelete={() => { if(confirm('ลบไฟล์นี้?')) deleteMut.mutate(f.id) }} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function FileRow({ file, fmtSize, isAdmin, onDelete }: { file:AssetFileItem; fmtSize:(s:number|null)=>string; isAdmin:boolean; onDelete:()=>void }) {
  const isWinAudit = file.fileType === 'WINAUDIT'
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group text-xs hover:bg-gray-100">
      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${isWinAudit ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
        {isWinAudit ? <Monitor size={12}/> : <File size={12}/>}
      </div>
      <span className="flex-1 truncate text-gray-700">{file.originalName}</span>
      {isWinAudit && <span className="text-purple-500 text-xs bg-purple-50 px-1.5 py-0.5 rounded flex-shrink-0">WinAudit</span>}
      <span className="text-gray-400 flex-shrink-0">{fmtSize(file.size)}</span>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <a href={file.url} download={file.originalName} className="p-1 text-gray-400 hover:text-blue-600 rounded" title="ดาวน์โหลด">
          <Download size={12}/>
        </a>
        <a href={file.url} target="_blank" rel="noreferrer" className="p-1 text-gray-400 hover:text-blue-600 rounded" title="เปิด">
          <ExternalLink size={12}/>
        </a>
        {isAdmin && <button onClick={onDelete} className="p-1 text-gray-400 hover:text-red-600 rounded"><X size={12}/></button>}
      </div>
    </div>
  )
}

function InfoBlock({ label, value }: { label: string; value?: string|number|null }) {
  if (!value && value !== 0) return null
  return <div><dt className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</dt><dd className="text-sm text-gray-900 mt-0.5 font-medium break-all">{String(value)}</dd></div>
}

function Sec({ title, children, cols=3 }: { title:string; children:React.ReactNode; cols?:number }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4 pb-2 border-b border-gray-100">{title}</h2>
      <dl className={`grid grid-cols-2 md:grid-cols-${cols} gap-x-6 gap-y-4`}>{children}</dl>
    </div>
  )
}

type Tab = 'info'|'ma'|'components'|'tickets'|'logs'

interface ChecklistItem { id: string; label: string; checked: boolean }
const DEFAULT_CHECKLIST: ChecklistItem[] = [
  { id:'1', label:'ทำความสะอาดภายนอก', checked:false },
  { id:'2', label:'ตรวจสอบสายไฟและขั้วต่อ', checked:false },
  { id:'3', label:'ทดสอบการทำงานปกติ', checked:false },
  { id:'4', label:'อัปเดต Driver / Software', checked:false },
  { id:'5', label:'ตรวจสอบพัดลมและระบบระบายความร้อน', checked:false },
]
const emptyMaForm = { type:'Preventive', description:'', cost:'', performedBy:'', nextDate:'', date:'' }

function AssetTicketsTab({ assetId }: { assetId: string }) {
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ title: '', type: 'INCIDENT', priority: 'MEDIUM', description: '' })
  const [creating, setCreating] = useState(false)
  const qc = useQueryClient()

  const { data, refetch } = useQuery({
    queryKey: ['tickets', 'asset', assetId],
    queryFn: () => api.get('/tickets', { params: { assetId, limit: 20 } }).then(r => r.data),
  })
  const { data: categories = [] } = useQuery<any[]>({ queryKey: ['ticket-categories'], queryFn: () => api.get('/tickets/categories').then(r => r.data) })

  const createTicket = async () => {
    if (!form.title.trim()) return
    setCreating(true)
    try {
      await api.post('/tickets', { ...form, assetId })
      setForm({ title: '', type: 'INCIDENT', priority: 'MEDIUM', description: '' })
      setShowCreate(false)
      refetch()
      qc.invalidateQueries({ queryKey: ['ticket-stats'] })
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setCreating(false) }
  }

  const tickets: any[] = data?.tickets || []

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">Tickets ที่เกี่ยวข้อง</h2>
        <button onClick={() => setShowCreate(v => !v)}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
          <Plus size={13}/> สร้าง Ticket
        </button>
      </div>

      {showCreate && (
        <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-blue-700">สร้าง Ticket ใหม่สำหรับ Asset นี้</p>
          <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="หัวข้อปัญหา/คำขอ *"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
              <option value="INCIDENT">Incident</option>
              <option value="SERVICE_REQUEST">Service Request</option>
            </select>
            <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>
          <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="รายละเอียดเพิ่มเติม (ไม่บังคับ)"/>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50">ยกเลิก</button>
            <button onClick={createTicket} disabled={creating || !form.title.trim()}
              className="px-4 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {creating ? 'กำลังสร้าง...' : 'สร้าง'}
            </button>
          </div>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Inbox size={28} className="mx-auto mb-2 text-gray-300"/>
          <p className="text-sm">ยังไม่มี Ticket สำหรับ Asset นี้</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t: any) => (
            <div key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}
              className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors">
              <span className="font-mono text-xs text-gray-400 flex-shrink-0">#{String(t.ticketNumber).padStart(4,'0')}</span>
              <p className="flex-1 text-sm text-gray-800 truncate">{t.title}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${PRIORITY_CFG[t.priority as TicketPriority]?.color}`}>
                {PRIORITY_CFG[t.priority as TicketPriority]?.label}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_CFG[t.status as TicketStatus]?.color}`}>
                {STATUS_CFG[t.status as TicketStatus]?.label}
              </span>
            </div>
          ))}
          <Link to={`/tickets?assetId=${assetId}`} className="block text-center text-xs text-blue-600 hover:underline pt-1">ดูทั้งหมดใน ServiceDesk →</Link>
        </div>
      )}
    </div>
  )
}

export default function AssetDetailPage({ isAdmin, canManageAssets }: { isAdmin: boolean; canManageAssets: boolean }) {
  const { id } = useParams<{id:string}>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('info')
  const [qrOpen, setQrOpen] = useState(false)
  const [logPage, setLogPage] = useState(1)
  const [lightbox, setLightbox] = useState<string|null>(null)
  const [maForm, setMaForm] = useState(emptyMaForm)
  const [maFormOpen, setMaFormOpen] = useState(false)
  const [maError, setMaError] = useState<string|null>(null)
  const [checklist, setChecklist] = useState<ChecklistItem[]>([])
  const [newCheckItem, setNewCheckItem] = useState('')
  const [pendingImages, setPendingImages] = useState<File[]>([])
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([])
  const [maImageId, setMaImageId] = useState<string|null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const maFileRef = useRef<HTMLInputElement>(null)
  const maNewFileRef = useRef<HTMLInputElement>(null)

  const { data: asset, isLoading, error } = useQuery<Asset>({
    queryKey: ['asset', id],
    queryFn: () => api.get(`/assets/${id}`).then(r => r.data),
    enabled: !!id,
  })

  const { data: glpiUrl } = useQuery<string>({
    queryKey: ['glpi-url'],
    queryFn: () => api.get('/glpi/url').then(r => r.data.url),
    enabled: !!asset?.glpiId,
  })

  const { data: companies = [] } = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','companies'], queryFn: () => api.get('/masterdata/companies').then(r=>r.data).catch(()=>[]) })
  const { data: allSites = [] }  = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','sites'],     queryFn: () => api.get('/masterdata/sites').then(r=>r.data).catch(()=>[]) })
  const { data: allDepts = [] }  = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','departments'],queryFn: () => api.get('/masterdata/departments').then(r=>r.data).catch(()=>[]) })

  const resolvedCompany = companies.find(c => c.id === (asset as any)?.companyId)?.name
  const resolvedSite    = allSites.find(s => s.id === (asset as any)?.siteId)?.name
  const resolvedDept    = allDepts.find(d => d.id === (asset as any)?.departmentId)?.name

  const deleteAsset = useMutation({
    mutationFn: () => api.delete(`/assets/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assets'] }); navigate('/assets') },
    onError: (e: any) => alert(e?.response?.data?.message || 'ลบไม่สำเร็จ'),
  })

  const [imgError, setImgError] = useState<string|null>(null)
  const uploadImages = useMutation({
    mutationFn: (files: FileList) => {
      const fd = new FormData()
      Array.from(files).forEach(f => fd.append('images', f))
      return api.post(`/assets/${id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['asset', id] }); setImgError(null) },
    onError: (e: any) => setImgError(e?.response?.data?.message || 'อัพโหลดไม่สำเร็จ'),
  })

  const deleteImage = useMutation({
    mutationFn: (imageId: string) => api.delete(`/assets/images/${imageId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['asset', id] }),
  })

  const { data: vendors = [] } = useQuery<{id:string;name:string}[]>({ queryKey: ['vendors'], queryFn: () => api.get('/vendors').then(r=>r.data) })

  const { data: deliveryDocs = [] } = useQuery<any[]>({
    queryKey: ['deliveries', 'asset', id],
    queryFn: () => api.get(`/deliveries?assetId=${id}`).then(r => r.data),
    enabled: !!id,
  })

  const { data: assetSoftware = [] } = useQuery<any[]>({
    queryKey: ['asset-software', id],
    queryFn: () => api.get(`/software/asset/${id}`).then(r => r.data),
    enabled: !!id,
  })

  const { data: logsData } = useQuery<{logs:{id:string;action:string;detail?:string;createdAt:string}[];total:number;pages:number}>({
    queryKey: ['asset-logs', id, logPage],
    queryFn: () => api.get(`/logs/${id}?page=${logPage}&limit=20`).then(r => r.data),
    enabled: !!id && tab === 'logs',
  })

  const { data: templates = [], refetch: refetchTpl } = useQuery<{id:string;name:string;items:ChecklistItem[]}[]>({
    queryKey: ['ma-templates'], queryFn: () => api.get('/maintenance/templates').then(r=>r.data)
  })

  const addMa = useMutation({
    mutationFn: async (form: typeof emptyMaForm) => {
      const row = await api.post('/maintenance', {
        assetId: id, ...form,
        cost: form.cost ? parseFloat(form.cost) : undefined,
        date: form.date || undefined,
        nextDate: form.nextDate || undefined,
        checklist,
      })
      if (pendingImages.length > 0) {
        const fd = new FormData()
        pendingImages.forEach(f => fd.append('images', f))
        await api.post(`/maintenance/${row.data.id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      return row
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asset', id] })
      setMaForm(emptyMaForm)
      setChecklist([])
      setPendingImages([])
      setPendingPreviews([])
      setMaFormOpen(false)
      setMaError(null)
    },
    onError: (e: any) => setMaError(e?.response?.data?.message || e?.message || 'เกิดข้อผิดพลาด'),
  })

  const deleteMa = useMutation({
    mutationFn: (maId: string) => api.delete(`/maintenance/${maId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['asset', id] }),
  })

  const uploadMaImages = useMutation({
    mutationFn: ({ maId, files }: { maId: string; files: FileList }) => {
      const fd = new FormData()
      Array.from(files).forEach(f => fd.append('images', f))
      return api.post(`/maintenance/${maId}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['asset', id] }),
  })

  const deleteMaImage = useMutation({
    mutationFn: (imgId: string) => api.delete(`/maintenance/images/${imgId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['asset', id] }),
  })

  if (isLoading) return <div className="p-8 text-center text-gray-400">กำลังโหลด...</div>
  if (error || !asset) return <div className="p-8 text-center text-gray-400">ไม่พบ Asset</div>

  const statusColor = STATUS_COLORS[asset.status] || 'gray'

  return (
    <div className="max-w-5xl mx-auto flex flex-col h-full">
      {/* Sticky header + tabs */}
      <div className="sticky top-0 z-10 bg-gray-50 px-6 pt-6 pb-0 flex-shrink-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/assets')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
              <ArrowLeft size={18}/>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">{asset.assetCode} <DataSourceBadge source={(asset as any).dataSource}/></h1>
              <p className="text-sm text-gray-500">{asset.name || '-'}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-${statusColor}-100 text-${statusColor}-700`}>
              {ASSET_STATUS_LABELS[asset.status] || asset.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(asset as any).glpiId && glpiUrl && (
              <a href={`${glpiUrl.replace(/\/+$/,'')}/front/computer.form.php?id=${(asset as any).glpiId}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                <ExternalLink size={15}/> เปิดใน GLPI
              </a>
            )}
            <button onClick={() => setQrOpen(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
              <QrCode size={15}/> QR Code
            </button>
            {canManageAssets && (
              <button onClick={() => navigate(`/assets/${id}/edit`)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm">
                <Edit2 size={15}/> แก้ไข
              </button>
            )}
            {canManageAssets && (
              <button onClick={() => { if(confirm('ลบ Asset นี้?')) deleteAsset.mutate() }} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm">
                <Trash2 size={15}/> ลบ
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 flex-wrap">
          {(['info','ma','components','tickets','logs'] as Tab[]).map(t => (
            <button key={t} onClick={() => { setTab(t); if(t==='logs') setLogPage(1) }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${tab===t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
              {t === 'info' ? '📋 ข้อมูล' : t === 'ma' ? `🔧 MA (${((asset as any).maintenance||[]).length})` : t === 'components' ? '🔩 Components' : t === 'tickets' ? '🎫 Tickets' : '📝 ประวัติ'}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4 space-y-4">

      {/* Info Tab */}
      {tab === 'info' && (
        <div className="space-y-4">
          <Sec title="ข้อมูลพื้นฐาน">
            <InfoBlock label="Asset Code" value={asset.assetCode}/>
            <InfoBlock label="Asset Tag" value={asset.assetTag}/>
            <InfoBlock label="Serial Number" value={asset.serialNumber}/>
            <InfoBlock label="ประเภท" value={ASSET_TYPE_LABELS[asset.type] || asset.type}/>
            <InfoBlock label="หมวดหมู่" value={asset.category}/>
            <InfoBlock label="ยี่ห้อ" value={asset.brand}/>
            <InfoBlock label="Model" value={asset.model}/>
            <InfoBlock label="แหล่งที่มาของข้อมูล" value={{ GLPI: 'ซิงก์จาก GLPI Agent', WINAUDIT: 'นำเข้าจาก WinAudit', CSV: 'นำเข้าจาก CSV' }[(asset as any).dataSource as string] || 'กรอกเอง'}/>
            <InfoBlock label="Spec" value={asset.spec}/>
          </Sec>
          <Sec title="ตำแหน่ง & ผู้รับผิดชอบ">
            <InfoBlock label="บริษัท" value={resolvedCompany || (asset as any).company || (asset as any).companyId}/>
            <InfoBlock label="Site" value={resolvedSite || (asset as any).site || (asset as any).siteId}/>
            <InfoBlock label="แผนก" value={resolvedDept || (asset as any).department || (asset as any).departmentId}/>
            <InfoBlock label="อาคาร" value={asset.building}/>
            <InfoBlock label="ชั้น" value={asset.floor}/>
            <InfoBlock label="ห้อง" value={asset.room}/>
            <InfoBlock label="รหัสพนักงาน" value={asset.employeeId}/>
            <InfoBlock label="ผู้ใช้งาน" value={asset.assignedTo}/>
          </Sec>
          <Sec title="ข้อมูลการจัดซื้อ">
            <InfoBlock label="วันที่ซื้อ" value={asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString('th-TH-u-ca-gregory') : null}/>
            <InfoBlock label="ราคาซื้อ" value={asset.purchasePrice ? `฿${Number(asset.purchasePrice).toLocaleString()}` : null}/>
            <InfoBlock label="ประกันหมด" value={asset.warrantyExpiry ? new Date(asset.warrantyExpiry).toLocaleDateString('th-TH-u-ca-gregory') : null}/>
          </Sec>
          <Sec title="การซ่อมบำรุง (MA)">
            <InfoBlock label="MA ล่าสุด" value={(asset as any).maDate ? new Date((asset as any).maDate).toLocaleDateString('th-TH-u-ca-gregory') : null}/>
            <InfoBlock label="MA ครั้งถัดไป" value={(asset as any).nextMaDate ? new Date((asset as any).nextMaDate).toLocaleDateString('th-TH-u-ca-gregory') : null}/>
            <InfoBlock label="จำนวนครั้ง MA" value={((asset as any).maintenance||[]).length || null}/>
          </Sec>

          {/* Delivery Docs */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2">
                  <ClipboardList size={14}/> ใบส่งมอบทรัพย์สิน
                </h2>
                {(() => {
                  if (!asset.assignedTo) return null
                  const latestDoc = [...deliveryDocs].sort((a: any, b: any) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
                  const hasDoc = latestDoc?.employeeName === asset.assignedTo && latestDoc?.docStatus !== 'CANCELLED'
                  return hasDoc
                    ? <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ มีใบส่งมอบแล้ว</span>
                    : <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">⚠ ยังไม่มีใบส่งมอบ</span>
                })()}
              </div>
              {isAdmin && (
                <button onClick={() => navigate(`/deliveries/new?assetId=${id}`)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Plus size={13}/> สร้างใบส่งมอบ
                </button>
              )}
            </div>
            {deliveryDocs.length === 0 ? (
              <div className="flex flex-col items-center py-6 text-gray-300">
                <ClipboardList size={28} className="mb-2"/>
                <p className="text-sm text-gray-400">ยังไม่มีใบส่งมอบ</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {deliveryDocs.map((doc: any) => (
                  <div key={doc.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          {doc.docNumber ? <span className="font-mono text-blue-600">{doc.docNumber}</span> : null}
                          {doc.employeeName || '—'}
                          {(() => {
                            const s = doc.docStatus || 'PENDING'
                            const cfg: Record<string,string> = {
                              PENDING:   'bg-amber-50 text-amber-700 border border-amber-200',
                              COMPLETED: 'bg-green-50 text-green-700 border border-green-200',
                              CANCELLED: 'bg-red-50 text-red-600 border border-red-200',
                            }
                            const lbl: Record<string,string> = { PENDING:'รอแนบไฟล์', COMPLETED:'มีใบส่งมอบ', CANCELLED:'ยกเลิก' }
                            return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg[s] || cfg.PENDING}`}>{lbl[s] || s}</span>
                          })()}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {doc.location ? `${doc.location}` : ''}
                          {doc.contractEnd ? ` · หมดสัญญา ${new Date(doc.contractEnd).toLocaleDateString('th-TH-u-ca-gregory')}` : ''}
                          {doc.attachments?.length > 0 && <span className="ml-1 text-blue-500">· {doc.attachments.length} ไฟล์</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => window.open(`/deliveries/${doc.id}/print`, '_blank')}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-colors">
                          <Printer size={13}/> พิมพ์
                        </button>
                        {isAdmin && (
                          <button onClick={() => navigate(`/deliveries/${doc.id}/edit`)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors">
                            <Edit2 size={13}/> แก้ไข
                          </button>
                        )}
                      </div>
                    </div>
                    <DeliveryAttachPanel
                      docId={doc.id}
                      attachments={doc.attachments || []}
                      onUploaded={() => qc.invalidateQueries({ queryKey: ['deliveries','asset', id] })}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Software Licenses */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2">
                <Package size={14}/> Software ที่ติดตั้ง ({assetSoftware.length})
              </h2>
              <a href="/software" className="text-xs text-blue-600 hover:underline">จัดการ Software →</a>
            </div>
            {assetSoftware.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">ยังไม่มี Software ที่ผูกกับ Asset นี้</p>
            ) : (
              <div className="space-y-1.5">
                {assetSoftware.map((sw: any) => (
                  <div key={sw.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${sw.softwareCategory==='OS'?'bg-blue-100 text-blue-700':'bg-gray-200 text-gray-600'}`}>
                      {sw.softwareCategory==='OS'?'OS':'App'}
                    </span>
                    <span className="text-sm font-medium text-gray-900 flex-1">{sw.name}</span>
                    {sw.version && <span className="text-xs text-gray-400">v{sw.version}</span>}
                    {sw.expiryDate && <span className={`text-xs ${new Date(sw.expiryDate)<new Date()?'text-red-600':'text-gray-400'}`}>
                      หมด {new Date(sw.expiryDate).toLocaleDateString('th-TH-u-ca-gregory')}
                    </span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">รูปภาพ</h2>
              {canManageAssets && (
                <div className="flex flex-col items-end gap-1">
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => { setImgError(null); e.target.files?.length && uploadImages.mutate(e.target.files) }} />
                  <button onClick={() => fileRef.current?.click()}
                    disabled={uploadImages.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                    <Upload size={14}/> {uploadImages.isPending ? 'กำลังอัพโหลด...' : 'เพิ่มรูป'}
                  </button>
                  {imgError && <p className="text-xs text-red-500">{imgError}</p>}
                </div>
              )}
            </div>
            {((asset as any).images||[]).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-300">
                <ImageIcon size={36} className="mb-2"/>
                <p className="text-sm">ยังไม่มีรูปภาพ</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {((asset as any).images as any[]).map((img: any) => (
                  <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
                    <img src={img.url} alt="" className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setLightbox(img.url)} />
                    {canManageAssets && (
                      <button onClick={() => deleteImage.mutate(img.id)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={12}/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* WinAudit & Attached Files */}
          <AssetFilesPanel assetId={id!} files={(asset as any).files || []} isAdmin={canManageAssets} onChanged={() => qc.invalidateQueries({ queryKey: ['asset', id] })} />
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-lg shadow-2xl"/>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 bg-white/20 rounded-full text-white hover:bg-white/30">
            <X size={20}/>
          </button>
        </div>
      )}

      {/* MA Tab */}
      {tab === 'ma' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">ประวัติการซ่อมบำรุง (MA)</h2>
              {canManageAssets && !maFormOpen && (
                <button onClick={() => { setMaFormOpen(true); setChecklist([]) }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                  <Plus size={14}/> บันทึก MA
                </button>
              )}
            </div>

            {/* Add MA Form */}
            {maFormOpen && (
              <div className="mb-5 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Wrench size={14}/> บันทึกการ MA ใหม่</h3>

                {/* Template selector */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">เลือก Checklist Template</label>
                  <select defaultValue="" onChange={e => {
                    const tpl = templates.find(t=>t.id===e.target.value)
                    if (tpl) setChecklist(tpl.items.map(i=>({...i,checked:false})))
                  }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                    <option value="">-- เลือก Template (ไม่บังคับ) --</option>
                    {templates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>

                {/* Date + type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">วันที่ MA</label>
                    <input type="date" value={maForm.date} onChange={e => setMaForm(f=>({...f,date:e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">ประเภท MA</label>
                    <select value={maForm.type} onChange={e => setMaForm(f=>({...f,type:e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                      <option>Preventive</option><option>Corrective</option><option>Predictive</option>
                    </select>
                  </div>
                </div>

                {/* Checklist */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-2 block">Checklist การ MA</label>
                  <div className="space-y-1.5 mb-2">
                    {checklist.map(item => (
                      <div key={item.id} className="flex items-center gap-2">
                        <input type="checkbox" id={`cl-${item.id}`} checked={item.checked}
                          onChange={e => setChecklist(c => c.map(i => i.id===item.id ? {...i,checked:e.target.checked} : i))}
                          className="w-4 h-4 rounded text-blue-600"/>
                        <label htmlFor={`cl-${item.id}`} className={`text-sm flex-1 ${item.checked?'line-through text-gray-400':'text-gray-700'}`}>{item.label}</label>
                        <button onClick={() => setChecklist(c => c.filter(i => i.id!==item.id))} className="text-gray-300 hover:text-red-400"><X size={12}/></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={newCheckItem} onChange={e => setNewCheckItem(e.target.value)}
                      onKeyDown={e => { if(e.key==='Enter'&&newCheckItem.trim()){setChecklist(c=>[...c,{id:Date.now().toString(),label:newCheckItem.trim(),checked:false}]);setNewCheckItem('')}}}
                      placeholder="เพิ่มรายการ..." className="flex-1 border border-dashed border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    <button onClick={()=>{if(newCheckItem.trim()){setChecklist(c=>[...c,{id:Date.now().toString(),label:newCheckItem.trim(),checked:false}]);setNewCheckItem('')}}}
                      className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"><Plus size={14}/></button>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">รายละเอียดเพิ่มเติม</label>
                  <textarea value={maForm.description} onChange={e => setMaForm(f=>({...f,description:e.target.value}))} rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="อธิบายงานที่ทำ..."/>
                </div>

                {/* Cost + performer + nextDate */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">ค่าใช้จ่าย (฿)</label>
                    <input type="number" value={maForm.cost} onChange={e => setMaForm(f=>({...f,cost:e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0"/>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">ผู้ดำเนินการ</label>
                    <input value={maForm.performedBy} onChange={e => setMaForm(f=>({...f,performedBy:e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ชื่อช่าง / vendor..."/>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-gray-600 mb-1 block">กำหนด MA ครั้งถัดไป</label>
                    <input type="date" value={maForm.nextDate} onChange={e => setMaForm(f=>({...f,nextDate:e.target.value}))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                  </div>
                </div>

                {/* Inline image upload */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-2 block">รูปภาพประกอบ</label>
                  <input ref={maNewFileRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => {
                      if (!e.target.files?.length) return
                      const files = Array.from(e.target.files)
                      setPendingImages(p => [...p, ...files])
                      files.forEach(f => { const r = new FileReader(); r.onload=ev=>setPendingPreviews(p=>[...p,ev.target!.result as string]); r.readAsDataURL(f) })
                    }}/>
                  {pendingPreviews.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {pendingPreviews.map((src,i) => (
                        <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                          <img src={src} alt="" className="w-full h-full object-cover"/>
                          <button onClick={() => { setPendingImages(p=>p.filter((_,j)=>j!==i)); setPendingPreviews(p=>p.filter((_,j)=>j!==i)) }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X size={10}/></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => maNewFileRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600">
                    <Upload size={14}/> เพิ่มรูปภาพ {pendingImages.length > 0 && `(${pendingImages.length} ไฟล์)`}
                  </button>
                </div>

                {maError && (
                  <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{maError}</div>
                )}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => { setMaFormOpen(false); setMaForm(emptyMaForm); setChecklist([]); setPendingImages([]); setPendingPreviews([]); setMaError(null) }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm">ยกเลิก</button>
                  <button onClick={() => { setMaError(null); addMa.mutate(maForm) }} disabled={addMa.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                    {addMa.isPending ? 'กำลังบันทึก...' : 'บันทึก MA'}
                  </button>
                </div>
              </div>
            )}

            {/* MA History */}
            {((asset as any).maintenance||[]).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-300">
                <Wrench size={36} className="mb-2"/>
                <p className="text-sm">ยังไม่มีประวัติ MA</p>
              </div>
            ) : (
              <div className="space-y-4">
                {((asset as any).maintenance as any[]).map((m: any) => {
                  const cl: ChecklistItem[] = m.checklist || []
                  const done = cl.filter(i=>i.checked).length
                  return (
                    <div key={m.id} className="border border-gray-100 rounded-xl overflow-hidden">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3 p-4 bg-gray-50">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0"><Wrench size={13} className="text-blue-600"/></div>
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{m.type}</span>
                          <span className="text-xs text-gray-600 font-medium">{new Date(m.date).toLocaleDateString('th-TH-u-ca-gregory', {day:'2-digit',month:'2-digit',year:'numeric'})}</span>
                          {m.performedBy && <span className="text-xs text-gray-500">โดย: {m.performedBy}</span>}
                          {m.cost > 0 && <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">฿{Number(m.cost).toLocaleString()}</span>}
                          {cl.length > 0 && <span className="text-xs text-gray-500">{done}/{cl.length} รายการ</span>}
                        </div>
                        {canManageAssets && (
                          <button onClick={() => { if(confirm('ลบรายการ MA นี้?')) deleteMa.mutate(m.id) }} className="p-1.5 hover:bg-red-50 rounded-lg flex-shrink-0"><X size={14} className="text-red-400"/></button>
                        )}
                      </div>
                      <div className="p-4 space-y-3">
                        {m.description && <p className="text-sm text-gray-700">{m.description}</p>}
                        {m.nextDate && <p className="text-xs text-amber-600 font-medium">📅 MA ครั้งถัดไป: {new Date(m.nextDate).toLocaleDateString('th-TH-u-ca-gregory', {day:'2-digit',month:'2-digit',year:'numeric'})}</p>}

                        {/* Checklist display */}
                        {cl.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Checklist</p>
                            <div className="grid grid-cols-2 gap-1">
                              {cl.map((item: ChecklistItem) => (
                                <div key={item.id} className="flex items-center gap-1.5 text-xs">
                                  <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${item.checked?'bg-green-500 text-white':'bg-gray-200 text-gray-400'}`}>
                                    {item.checked ? '✓' : ''}
                                  </span>
                                  <span className={item.checked?'line-through text-gray-400':'text-gray-600'}>{item.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Images */}
                        {(m.images||[]).length > 0 && (
                          <div className="grid grid-cols-4 gap-2">
                            {(m.images as any[]).map((img:any) => (
                              <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-100">
                                <img src={img.url} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(img.url)}/>
                                {canManageAssets && <button onClick={() => deleteMaImage.mutate(img.id)} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X size={10}/></button>}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Upload images for this MA record */}
                        {canManageAssets && (
                          <div>
                            <input ref={maImageId===m.id?maFileRef:undefined} type="file" accept="image/*" multiple className="hidden"
                              onChange={e => e.target.files?.length && uploadMaImages.mutate({ maId: m.id, files: e.target.files })}/>
                            <button onClick={() => { setMaImageId(m.id); setTimeout(()=>maFileRef.current?.click(),50) }}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                              <Upload size={12}/> เพิ่มรูป MA
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tickets Tab */}
      {tab === 'tickets' && id && <AssetTicketsTab assetId={id}/>}

      {/* Components Tab */}
      {tab === 'components' && id && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <ComponentsTab assetId={id} isAdmin={canManageAssets}/>
        </div>
      )}

      {/* Logs Tab */}
      {tab === 'logs' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              ประวัติการเปลี่ยนแปลง {logsData ? `(${logsData.total} รายการ)` : ''}
            </h2>
            {logsData && logsData.pages > 1 && (
              <span className="text-xs text-gray-400">หน้า {logPage} / {logsData.pages}</span>
            )}
          </div>

          {logsData?.logs.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">ยังไม่มีประวัติ</p>
          )}

          <div className="space-y-0">
            {(logsData?.logs || []).map((log, idx) => (
              <div key={log.id} className={`flex items-start gap-3 py-3 ${idx < (logsData?.logs.length||0)-1 ? 'border-b border-gray-50' : ''}`}>
                <div className="flex-shrink-0 mt-0.5">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase ${
                    log.action === 'UPDATE' ? 'bg-blue-50 text-blue-600' :
                    log.action === 'MAINTENANCE' ? 'bg-amber-50 text-amber-600' :
                    log.action === 'CREATE' ? 'bg-green-50 text-green-600' :
                    'bg-gray-50 text-gray-500'
                  }`}>{log.action}</span>
                </div>
                <div className="flex-1 min-w-0">
                  {log.detail && <p className="text-sm text-gray-700 break-words">{log.detail}</p>}
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                  {new Date(log.createdAt).toLocaleString('th-TH-u-ca-gregory', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                </span>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {logsData && logsData.pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <button onClick={() => setLogPage(p => Math.max(1, p-1))} disabled={logPage <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                ← ก่อนหน้า
              </button>
              <div className="flex gap-1">
                {Array.from({ length: logsData.pages }, (_, i) => i+1).map(p => (
                  <button key={p} onClick={() => setLogPage(p)}
                    className={`w-8 h-8 text-xs rounded-lg ${p===logPage ? 'bg-blue-600 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-600'}`}>
                    {p}
                  </button>
                ))}
              </div>
              <button onClick={() => setLogPage(p => Math.min(logsData.pages, p+1))} disabled={logPage >= logsData.pages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                ถัดไป →
              </button>
            </div>
          )}
        </div>
      )}

      </div>{/* end scrollable content */}

      {qrOpen && <QRCodeModal assets={[asset]} onClose={() => setQrOpen(false)}/>}
    </div>
  )
}
